import React, { useState, useEffect } from 'react';
import { Search, PackageOpen, FileText, User, Hash, Clock, MapPin, ChevronRight, Activity, CalendarDays } from 'lucide-react';
import { submissionService } from '../services/adminAuth';
import type { Submission } from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';

const SearchSubmission: React.FC = () => {
    const { user } = useAuth();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [results, setResults] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    // Only allow System Admin (3) and TAG Chairman (5)
    const isAuthorized = user?.roleId === 3 || user?.roleId === 5;

    // Debounce the search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 500);
        return () => clearTimeout(handler);
    }, [query]);

    useEffect(() => {
        if (!isAuthorized) return;

        const performSearch = async () => {
            if (!debouncedQuery.trim()) {
                setResults([]);
                setSearched(false);
                return;
            }

            setLoading(true);
            setSearched(true);
            try {
                const res = await submissionService.searchSubmissions({
                    pageNumber: 1,
                    pageSize: 50,
                    search: debouncedQuery.trim()
                });

                if (res.isSuccess && res.data?.data) {
                    setResults(res.data.data);
                } else {
                    setResults([]);
                }
            } catch (err) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [debouncedQuery, isAuthorized]);

    if (!isAuthorized) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 text-red-500">
                    <MapPin size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
                <p className="text-gray-500 mt-2 max-w-md">You do not have permission to view the search portal. Only the System Administrator and TAG Chairman are authorized.</p>
            </div>
        );
    }

    const getWorkflowStageName = (stageNumber?: number) => {
        switch (stageNumber) {
            case 1: return "ES Registry";
            case 2: return "ES";
            case 3: return "DESS";
            case 4: return "TAG Divisional Head";
            case 5: return "TAG Chairman";
            case 6: return "Book Head";
            case 7: return "Journal Head";
            case 8: return "Thematic Area Leader";
            case 9: return "Thematic Area Members";
            case 10: return "Reviewer";
            case 11: return "ESS Staff";
            case 12: return "Author";
            default: return "Unknown Desk";
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8 animate-in fade-in duration-500">
            {/* Header section with Glassmorphism Search */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#088A61] to-[#055F42] p-8 md:p-12 text-center text-white isolate">
                {/* Decorative background elements */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-emerald-400/30 rounded-full mix-blend-overlay filter blur-3xl opacity-50 animate-blob"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#15803D]/40 rounded-full mix-blend-overlay filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>

                <div className="relative z-10 max-w-2xl mx-auto">
                    <h1 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">Search Submission </h1>
                    <p className="text-emerald-50 mb-8 font-medium text-lg">Locate any submission's current desk instantly by Number, Title, or Author(s) Name.</p>

                    <div className="relative flex items-center bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-2 shadow-inner focus-within:bg-white/20 focus-within:border-white/40 transition-all duration-300">
                        <div className="pl-4 pr-3 text-white/70">
                            <Search size={24} />
                        </div>
                        <input
                            type="text"
                            placeholder="Type a Submission Number, Author Name, or Title..."
                            className="flex-1 bg-transparent border-none text-white placeholder-white/50 focus:ring-0 text-lg py-3 outline-none"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {loading && (
                            <div className="pr-4">
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Results Area */}
            <div className="space-y-6">
                {loading && results.length === 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse flex flex-col gap-4">
                                <div className="flex justify-between items-start">
                                    <div className="w-1/2 h-5 bg-gray-200 rounded-md"></div>
                                    <div className="w-24 h-6 bg-gray-200 rounded-full"></div>
                                </div>
                                <div className="w-3/4 h-8 bg-gray-200 rounded-md"></div>
                                <div className="w-full h-4 bg-gray-100 rounded-md mt-2"></div>
                                <div className="w-1/3 h-4 bg-gray-100 rounded-md"></div>
                            </div>
                        ))}
                    </div>
                ) : searched && results.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm flex flex-col items-center">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                            <PackageOpen size={40} className="text-gray-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Submissions Found</h3>
                        <p className="text-gray-500 max-w-md">We couldn't find any records matching "{debouncedQuery}". Please double check the ID or spelling.</p>
                    </div>
                ) : results.length > 0 ? (
                    <>
                        <p className="text-sm font-semibold text-gray-500 px-2 uppercase tracking-widest">
                            Found {results.length} result{results.length !== 1 && 's'}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
                            {results.map(sub => (
                                <div key={sub.id} className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-[#088A61]/30 transition-all duration-300 flex flex-col relative overflow-hidden">
                                    {/* Accent line */}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#088A61] to-emerald-400 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>

                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-400 bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100">
                                            <Hash size={14} />
                                            {sub.submissionNumber || sub.id?.split('-')[0]}
                                        </div>
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm border ${sub.completedPrimaFacie ? 'bg-green-50 text-green-700 border-green-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
                                            Prima Facie: {sub.completedPrimaFacie ? 'Completed' : 'Not Completed'}
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-[#088A61] transition-colors">
                                        {sub.title}
                                    </h3>

                                    <div className="space-y-3 mt-auto pt-4">
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <User size={16} className="text-gray-400" />
                                            <span className="font-medium truncate">{sub.author?.person?.fullName || sub.authors || 'Unknown Author'}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-gray-600">
                                            <CalendarDays size={16} className="text-gray-400" />
                                            <span>Submitted: <span className="font-medium">{new Date(sub.submittedAt || Date.now()).toLocaleDateString('en-GB')}</span></span>
                                        </div>
                                    </div>

                                    {/* Current Desk Highlight */}
                                    <div className="mt-5 pt-4 border-t border-gray-100">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Current Desk</p>
                                                <div className="flex items-center gap-2">
                                                    <MapPin size={16} className="text-[#088A61]" />
                                                    <p className="text-sm font-bold text-gray-900">{getWorkflowStageName(sub.currentStage)}</p>
                                                </div>
                                            </div>
                                            <button className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-[#088A61] group-hover:text-white transition-colors">
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center opacity-50">
                        <Search size={48} className="text-gray-300 mb-4" />
                        <p className="text-lg font-semibold text-gray-400">Enter a query to start searching</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchSubmission;
